import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import {
  isDesktopBrowserLayout,
  isNativeApp,
} from "../../../common/hooks/useIsDesktopView";
import { getDeferredDeepLinkFingerprint } from "@juisci/device-fingerprint";
import { memoryStore, useStore } from "../../../lib/store";
import {
  apiClient,
  clearApiQuery,
  revalidateApiQuery,
  useApiQuery,
} from "../../../services/api";
import { GPT_LANDING_PATH } from "@juisci/gpt-handoff";
import { handedQuestionArrivedStore } from "../questionHandoff";
import { memorizePostLoginRedirectTo } from "../../app/utils/postLoginRedirect";
import { useSignupInFlight } from "../../auth/stores/signupInFlightStore";
import { useDesktopCodeRequest } from "../../auth/stores/desktopCodeRequestStore";
import { useCurrentUser } from "../../user/hooks/useCurrentUser";

/**
 * JuisciGPT for a LOGGED-OUT desktop visitor: the front door answers a couple
 * of questions before it asks for an account (PRD "Juisci GPT sans compte",
 * Notion roadmap). The API owns the ceilings (`service/anonymous-gpt-usage.ts`);
 * this module owns when the page replaces the sign-in gate, and the claim that
 * moves the visitor's conversations into the account they sign in to.
 */

/**
 * What follows the answer on the logged-out page: an account on a desktop
 * browser, the app on mobile web, where the browser signs nobody in
 * (`domains/app/appInstallWall`).
 */
export type AnonymousGptOffer = "signup" | "install";

/**
 * Who stands at the JuisciGPT landing (`/gpt/chat/new`) for this reader:
 *  - `open`: the logged-out JuisciGPT page, for a logged-out visitor on a
 *    desktop browser, or on mobile web when the website handed a question
 *    over on this page load, with the offer that follows the answer;
 *  - `closed`: everything else, including any other path, a member, the
 *    native shell, an auth state not resolved yet, and a code awaited from the
 *    door.
 *
 * A Google or Apple sign-in in flight (`signupInFlight`) reads as loading in
 * `useCurrentUser`, but it is still the visitor on the page who opened the
 * popup: the page stays, so a cancelled popup leaves their answer on screen.
 * Mobile web has no such popup: its offer is the install.
 */
export function useAnonymousGptFrontDoor():
  { kind: "closed" } | { kind: "open"; offer: AnonymousGptOffer } {
  const location = useLocation();
  const { isLoading, isAuthenticated } = useCurrentUser();
  const signupInFlight = useSignupInFlight();
  const codeRequest = useDesktopCodeRequest();
  const [handedQuestionArrived] = useStore(handedQuestionArrivedStore);

  if (location.pathname !== GPT_LANDING_PATH) return { kind: "closed" };
  if (isAuthenticated) return { kind: "closed" };
  // The native shell signs its reader in at `/welcome`: it has nothing to
  // install.
  if (isNativeApp()) return { kind: "closed" };
  if (!isDesktopBrowserLayout()) {
    // A question the website handed over is answered here before the install
    // ask, instead of the install wall standing in front of it. With no
    // question, the install wall stands: the app is where one asks.
    if (!handedQuestionArrived) return { kind: "closed" };
    if (isLoading) return { kind: "closed" };
    return { kind: "open", offer: "install" };
  }
  // The visitor left this page for the door and is typing the code it sent
  // them: JuisciGPT stands behind the code modal, not the page that would send
  // them back to the door.
  if (codeRequest !== null) return { kind: "closed" };
  if (isLoading && !signupInFlight) return { kind: "closed" };
  return { kind: "open", offer: "signup" };
}

type DeviceFingerprint =
  | { state: "pending" }
  // `value` is null when the browser refused the reads. A privacy browser is
  // not an attacker: the API then counts it on the cookie and the network.
  | { state: "ready"; value: string | null };

const fingerprintStore = memoryStore<DeviceFingerprint>({ state: "pending" });
let fingerprintRead: Promise<void> | null = null;

/**
 * The browser's fingerprint, read once per page load. The read is local (a
 * hash of navigator and screen properties, no request) and its result is part
 * of the chat's identity, so the page waits for it.
 */
export function useDeviceFingerprint(): DeviceFingerprint {
  const [fingerprint] = useStore(fingerprintStore);
  if (fingerprintRead === null)
    fingerprintRead = getDeferredDeepLinkFingerprint().then(
      ({ fingerprint: value }) => {
        fingerprintStore.set({ state: "ready", value });
      },
      () => {
        fingerprintStore.set({ state: "ready", value: null });
      }
    );
  return fingerprint;
}

/** Today's allowance of this visitor. */
export function useAnonymousAllowance(
  device: string | null
): Exclude<
  ReturnType<typeof useApiQuery<"/webapp/public/gpt/allowance">>,
  { state: "idle" }
> {
  return useApiQuery("/webapp/public/gpt/allowance", {
    query: device === null ? {} : { device },
    // The visitor cookie the ceilings count on.
    credentials: "include",
  });
}

/**
 * Send the login and the signup started from the logged-out page back to
 * JuisciGPT, where `resolvePostAuthDestination` opens the conversation the
 * visitor just had, claimed into their account.
 */
export function returnToJuisciGptAfterSignIn(): void {
  memorizePostLoginRedirectTo(GPT_LANDING_PATH);
}

// One claim per account and page load: the promise is shared, so the boot
// call (`useClaimAnonymousConversations`) and the post-sign-in destination
// (`resolvePostAuthDestination`) wait on the same request.
let claim: { userId: string; conversationIds: Promise<string[]> } | null = null;

/**
 * Move the conversations this browser had while logged out into the account
 * now signed in, newest first, and mark the browser as a member's (the API
 * then asks it to log in rather than offering anonymous questions). The cookies
 * that say both are on the API host, hence `credentials: "include"`.
 *
 * A failed claim answers `[]` for the rest of the page load: the conversations
 * stay with the visitor, and the next load claims them. A claim that moved some
 * refreshes the conversation history, which may already be on screen.
 */
export function claimAnonymousConversations(userId: string): Promise<string[]> {
  if (claim !== null && claim.userId === userId) return claim.conversationIds;
  const conversationIds = apiClient
    .post("/webapp/private/gpt/anonymous/claim", { credentials: "include" })
    .then(
      ({ body }) => {
        if (body.conversationIds.length > 0) {
          void revalidateApiQuery("/webapp/private/conversations");
          clearApiQuery("/webapp/private/conversations/latest-topic", {});
        }
        return body.conversationIds;
      },
      (): string[] => []
    );
  claim = { userId, conversationIds };
  return conversationIds;
}

/**
 * Claim once the signed-in user is known, on every load and wherever it lands:
 * the member cookie has to reach browsers whose session predates it, and an
 * answer still streaming when the visitor signed up is saved after the first
 * claim.
 */
export function useClaimAnonymousConversations(userId: string | null): void {
  useEffect(() => {
    if (userId !== null) void claimAnonymousConversations(userId);
  }, [userId]);
}
